import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classnames from 'classnames';
import isFunction from 'lodash/isFunction';
import isNil from 'lodash/isNil';

import { closeNotification, danger } from 'redux-store/reducers/common-reducer';
import {
  handleLoginResponse,
  handleLogoutResponse,
  setErrorAction,
  updateModalTypeAction
} from './redux/actions';

import { __t, applyTranslations } from 'utils/language';
import { setItem } from 'utils/storage/sessionStorage';
import {
  CREATE_PROFILE_TEMP_AUTH_STORAGE_KEY,
  DEFAULT_FB_OBJECT
} from './utils/facebook-button';

import './facebook-button.scss';

applyTranslations(require('./facebook-button.i18n.json'));

import type { State } from 'types';
import type {
  FacebookButtonAuthResponse,
  FacebookButtonStatus,
  FacebookButtonStatusResponse,
  FacebookButtonWindow
} from './types';

interface Props {
  isCreationStep?: boolean,
  isCentered?: boolean,
  size?: 'small' | 'normal',
  onSuccessfulLogin?: (isCreationStep?: boolean) => void,
  onLogout?: () => void
}

declare let window: FacebookButtonWindow;

const SCOPES = 'pages_read_engagement,pages_show_list,instagram_basic,instagram_manage_insights,business_management';
const MSG_KEY = 'facebook_button';

const FacebookButton: React.FC<Props> = ({
  isCreationStep,
  isCentered,
  size,
  onSuccessfulLogin,
  onLogout
}): JSX.Element => {
  const FB = window.FB ?? DEFAULT_FB_OBJECT;
  const dispatch = useDispatch();
  const buttonState = useSelector((state: State) => state.facebookButton);
  const creatorProfile = useSelector((state: State) => state.influencerProfile.profile);
  const creatorProfileId = creatorProfile?.id ?? -1;
  const isLoggedIn = Boolean(buttonState?.isFBLoginActive);
  const isExpired = Boolean(buttonState?.isFBLoginExpired);
  const buttonText = __t(`${MSG_KEY}.buttons.${isLoggedIn ? 'logout' : 'continue_with'}`);

  const hasError = (
    authResponse: FacebookButtonAuthResponse,
    status: FacebookButtonStatus
  ): boolean => (
    status !== 'connected'
    || isNil(authResponse?.accessToken)
    || isNil(authResponse?.data_access_expiration_time)
    || isNil(authResponse?.userID)
  );

  const handleError = (): void => {
    const errorMessage = __t(`${MSG_KEY}.errors.handle_login`);

    dispatch(setErrorAction(errorMessage));
    dispatch(danger(errorMessage));
  };

  const handleSessionStorage = (
    authResponse: FacebookButtonAuthResponse
  ): void => {
    setItem(CREATE_PROFILE_TEMP_AUTH_STORAGE_KEY, JSON.stringify(authResponse));
  };

  const getStatus = (): Promise<FacebookButtonStatusResponse> => (
    new Promise(resolve => {
      FB.getLoginStatus(resolve);
    })
  );

  const logout = (): Promise<FacebookButtonStatusResponse> => (
    new Promise(resolve => {
      FB.logout(resolve);
    })
  );

  const handleSuccessfulLogin = (): void => {
    if (isFunction(onSuccessfulLogin)) {
      onSuccessfulLogin();
    }
  };

  const handleLogin = (): void => {
    FB.login(async () => {
      const { authResponse, status } = await getStatus();

      if (hasError(authResponse, status)) {
        handleError();
        return;
      }

      dispatch(setErrorAction(null));
      dispatch(closeNotification());

      if (isCreationStep) {
        handleSessionStorage(authResponse);
      }

      const response = await dispatch(handleLoginResponse(authResponse, creatorProfileId));

      // @ts-ignore for some reason, the return type for dispatch above is function
      if (response) {
        dispatch(updateModalTypeAction(null));
        handleSuccessfulLogin();
      }
    }, {
      scope: SCOPES,
      return_scopes: true
    });
  };

  const handleLogout = async (): Promise<void> => {
    const { status } = await getStatus();

    if (status !== 'unknown') {
      await logout();

      if (onLogout) {
        onLogout();
      }
    }

    dispatch(closeNotification);
    dispatch(handleLogoutResponse(creatorProfileId));
  };

  const handleForcedLogout = async (): Promise<void> => {
    const { status } = await getStatus();

    if ((!isLoggedIn || isExpired) && status !== 'unknown') {
      await logout();

      if (onLogout) {
        onLogout();
      }
    }
  };

  React.useEffect(() => {
    handleForcedLogout();

    if (!isNil(buttonState.error)) {
      dispatch(setErrorAction(null));
    }
  }, []);

  const containerClasses = classnames('facebook-button', {
    'facebook-button--logout': isLoggedIn,
    'facebook-button--centered': Boolean(isCentered),
    [`facebook-button--size-${size}`]: Boolean(size)
  });

  const logoClasses = classnames('facebook-button__logo', {
    [`facebook-button__logo--size-${size}`]: Boolean(size)
  });

  return (
    <div
      className={containerClasses}
      onClick={isLoggedIn ? handleLogout : handleLogin}
    >
      <div className={logoClasses}>
        <img
          src="/static/images/facebook-official-logo.png"
          className="lazyload"
          alt={buttonText}
        />
      </div>
      <div className="facebook-button__text">
        {buttonText}
      </div>
    </div>
  );
};

export default FacebookButton;
